import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEstadoDaCarga } from '../../design';
import { cnabService } from '../../services/api';
import { periodToParams } from '../PeriodSelector';
import { usePeriodoNaUrl } from '../../utils/periodoNaUrl';
import { useSortState } from '../shared/SortableTableHeader';
import {
  BoletoCardFilter,
  BoletoItem,
  boletoCapabilities,
  computeStats,
  matchesCardFilter,
} from './boletoCapabilities';

/**
 * Estado de dados da tela de Boletos: carga, erro, filtros, ordenação, seleção
 * e estatísticas. Tudo memoizado — antes o componente varria o array oito vezes
 * fora de `useMemo`, a cada render.
 */
export function useBoletos() {
  // O período na URL (15/10/2026): ?mes=… / ?de=…&ate=…; F5 e link colado mantêm o mês. Ver utils/periodoNaUrl.
  const [periodFilter, setPeriodFilter] = usePeriodoNaUrl();
  const [boletos, setBoletos] = useState<BoletoItem[]>([]);
  /**
   * A CARGA (12/10/2026, C1 e C2 da medição de Boletos). Antes: a recarga que falhava ZERAVA a lista (setBoletos([]))
   * e os cartões iam a 0; e a resposta de um mês velho podia chegar depois e sobrescrever a do mês novo. Agora:
   * - a recarga do MESMO recorte que falha mantém a lista lida, e a tela diz de quando ela é;
   * - a do recorte TROCADO que falha não deixa a lista do mês velho fingindo ser a do novo: limpa, e `lido` fica falso;
   * - só a leitura mais recente grava a lista.
   */
  const carga = useEstadoDaCarga();
  const ultimaLeitura = useRef(0);
  const [recorteDosDados, setRecorteDosDados] = useState<string | null>(null);
  const recorteDosDadosRef = useRef<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');
  const [cardFilter, setCardFilter] = useState<BoletoCardFilter>('all');
  const [sort, toggleSort] = useSortState({ col: 'dueDate', dir: 'asc' });

  const recorte = JSON.stringify(periodToParams(periodFilter));
  const { rodar } = carga;
  const reload = useCallback(async () => {
    const minha = ++ultimaLeitura.current;
    const p = periodToParams(periodFilter);
    const chave = JSON.stringify(p);
    await rodar(async () => {
      let data: any;
      try {
        data = await cnabService.getBoletos(p.month || '', p.startDate, p.endDate);
      } catch (e) {
        // Recorte trocado e sem leitura: a lista do recorte velho não fica na tela como se fosse deste.
        if (minha === ultimaLeitura.current && recorteDosDadosRef.current !== chave) {
          setBoletos([]);
          recorteDosDadosRef.current = null;
          setRecorteDosDados(null);
        }
        throw e;
      }
      if (minha !== ultimaLeitura.current) return; // resposta velha: a mais nova é que decide
      const list: BoletoItem[] = Array.isArray(data) ? data : [];
      setBoletos(list);
      recorteDosDadosRef.current = chave;
      setRecorteDosDados(chave);
      // Mantém selecionado só o que continua visível: ao trocar de período ou
      // filtro, ids invisíveis permaneciam marcados e entravam na remessa.
      const visiveis = new Set(list.map(b => b.id));
      setSelected(prev => {
        const next = new Set(Array.from(prev).filter(id => visiveis.has(id)));
        return next.size === prev.size ? prev : next;
      });
    });
  }, [periodFilter, rodar]);
  const lido = recorteDosDados !== null && recorteDosDados === recorte;
  const loading = carga.estado === 'carregando' && !lido;
  const loadError = carga.estado === 'erro' ? carga.erro : null;

  useEffect(() => {
    void reload();
  }, [reload]);

  const stats = useMemo(() => computeStats(boletos), [boletos]);

  const filtered = useMemo(() => {
    let result = boletos.filter(b => matchesCardFilter(b, cardFilter));

    const q = searchFilter.trim().toLowerCase();
    if (q) {
      result = result.filter(b =>
        (b.clientDisplayName || '').toLowerCase().includes(q) ||
        (b.clientName || '').toLowerCase().includes(q) ||
        (b.clientTradeName || '').toLowerCase().includes(q) ||
        (b.clientCnpj || '').toLowerCase().includes(q) ||
        (b.code || '').toLowerCase().includes(q) ||
        (b.nossoNumero || '').toLowerCase().includes(q)
      );
    }

    return [...result].sort((a, b) => {
      let va: any, vb: any;
      switch (sort.col) {
        case 'code': va = a.code; vb = b.code; break;
        case 'client':
          va = (a.clientDisplayName || a.clientTradeName || a.clientName || '').toLowerCase();
          vb = (b.clientDisplayName || b.clientTradeName || b.clientName || '').toLowerCase();
          break;
        case 'totalValue': va = a.totalValue || 0; vb = b.totalValue || 0; break;
        case 'dueDate': va = a.dueDate || ''; vb = b.dueDate || ''; break;
        case 'nossoNumero': va = a.nossoNumero || ''; vb = b.nossoNumero || ''; break;
        case 'boletoStatus': va = a.boletoStatus || ''; vb = b.boletoStatus || ''; break;
        case 'payment': va = a.boletoPaymentDate || ''; vb = b.boletoPaymentDate || ''; break;
        default: va = a.dueDate || ''; vb = b.dueDate || '';
      }
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [boletos, cardFilter, searchFilter, sort]);

  /**
   * Selecionáveis = subconjunto do que está NA TELA (filtro de card + busca).
   * Nasce do `filtered`, não da lista inteira: com busca ativa, o checkbox do
   * cabeçalho marcava os 96 do mês inteiro em vez da única linha visível.
   */
  const selectable = useMemo(() => filtered.filter(b => boletoCapabilities(b).isSelectable), [filtered]);

  const selectedBoletos = useMemo(() => boletos.filter(b => selected.has(b.id)), [boletos, selected]);

  const selection = useMemo(() => {
    const paraBaixa: number[] = [];
    let total = 0;
    for (const b of selectedBoletos) {
      const cap = boletoCapabilities(b);
      if (cap.canRemessaBaixa) paraBaixa.push(b.id);
      total += Number(b.totalValue) || 0;
    }
    return { paraBaixa, total, count: selectedBoletos.length };
  }, [selectedBoletos]);

  const toggleSelect = useCallback((id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(prev => {
      const next = new Set(prev);
      const allVisibleSelected = selectable.length > 0 && selectable.every(b => next.has(b.id));
      if (allVisibleSelected) selectable.forEach(b => next.delete(b.id));
      else selectable.forEach(b => next.add(b.id));
      return next;
    });
  }, [selectable]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  /**
   * E4 (12/10/2026, aprovado pelo Heitor; o remédio da #469 de Recebíveis): a seleção NÃO sobrevive à troca de cartão
   * nem de período. O "Baixar (N)" manda o que está marcado, e marcar num cartão e trocar de cartão deixava títulos
   * fora da tela dentro do lote. Troca desmarca tudo, e a tela diz quantos sumiram até a próxima marcação. A BUSCA não
   * desmarca de propósito: buscar A, marcar, buscar B e marcar é o uso legítimo do lote, e o modal da baixa lista todos.
   */
  const [desmarcados, setDesmarcados] = useState(0);
  const selecaoAtual = useRef(selected);
  selecaoAtual.current = selected;
  useEffect(() => {
    const n = selecaoAtual.current.size;
    if (n) { setDesmarcados(n); setSelected(new Set()); }
  }, [cardFilter, periodFilter]);
  useEffect(() => { if (selected.size) setDesmarcados(0); }, [selected]);

  /** E2: depois da baixa em lote com falha parcial, a seleção fica só com os que falharam (para tentar de novo). */
  const manterSo = useCallback((ids: number[]) => setSelected(new Set(ids)), []);

  return {
    periodFilter, setPeriodFilter,
    boletos, loading, loadError, reload,
    /** Há lista lida para o recorte da tela (sem ela, os cartões dizem "—", nunca 0). */
    lido,
    lidoEm: carga.atualizadoEm,
    searchFilter, setSearchFilter,
    cardFilter, setCardFilter,
    sort, toggleSort,
    stats, filtered, selectable,
    selected, selection, toggleSelect, toggleAll, clearSelection, manterSo,
    /** E4: quantos a última troca de cartão ou período desmarcou (0 depois de marcar de novo). */
    desmarcados,
  };
}
